import { useEffect, useRef } from 'react';
import { useTheme } from '@material-ui/core/styles';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
}

const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let particles: Particle[] = [];
    const connectionDistance = 130;
    const mouseRadius = 160;
    const mouse = { x: -9999, y: -9999 };

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
        : { r: 63, g: 167, b: 149 };
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const getParticleCount = () => {
      const area = window.innerWidth * window.innerHeight;
      return Math.min(Math.floor(area / 3500), 350);
    };

    // Sparse zone: the text/buttons area on the right (below logo).
    // Dots CAN exist here but are fewer and subtler.
    // The logo area itself is NOT excluded — dots flow over it.
    const getSparseZone = () => {
      const w = canvas.width;
      const h = canvas.height;
      const isMobile = w <= 768;

      if (isMobile) {
        // On mobile, sparse zone is the middle area with buttons
        return { x: w * 0.1, y: h * 0.35, w: w * 0.8, h: h * 0.35 };
      }
      // Desktop: just the text + buttons below the logo, not the logo itself
      return { x: w * 0.58, y: h * 0.32, w: w * 0.35, h: h * 0.35 };
    };

    // Returns 0-1 how deep into the sparse zone a point is (0 = outside, 1 = center)
    const sparseDepth = (px: number, py: number) => {
      const zone = getSparseZone();
      const cx = zone.x + zone.w / 2;
      const cy = zone.y + zone.h / 2;
      const halfW = zone.w / 2;
      const halfH = zone.h / 2;
      const dx = Math.abs(px - cx) / halfW;
      const dy = Math.abs(py - cy) / halfH;
      if (dx > 1 || dy > 1) return 0;
      return (1 - dx) * (1 - dy);
    };

    const createParticles = () => {
      const count = getParticleCount();
      const topBound = canvas.height * 0.6;
      particles = [];

      for (let i = 0; i < count; i++) {
        const x = Math.random() * canvas.width;
        // Bias toward top of the screen
        const y = Math.random() * topBound;

        // In the sparse zone, only keep ~25% of particles
        const depth = sparseDepth(x, y);
        if (depth > 0.2 && Math.random() > 0.25) {
          // Skip this particle — makes the zone sparser
          continue;
        }

        particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 2.2 + 0.8,
          opacity: Math.random() * 0.5 + 0.15,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const color = hexToRgb(theme.palette.primary.main);
      const fadeStart = canvas.height * 0.45;
      const fadeEnd = canvas.height * 0.75;

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const avgY = (particles[i].y + particles[j].y) / 2;
            const vertFade =
              avgY < fadeStart ? 1 : avgY > fadeEnd ? 0 : 1 - (avgY - fadeStart) / (fadeEnd - fadeStart);

            // Reduce connection opacity in sparse zone
            const avgDepth = (sparseDepth(particles[i].x, particles[i].y) + sparseDepth(particles[j].x, particles[j].y)) / 2;
            const sparseFade = 1 - avgDepth * 0.6;

            const alpha = (1 - dist / connectionDistance) * 0.15 * vertFade * sparseFade;
            if (alpha < 0.005) continue;

            ctx.beginPath();
            ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }

        // Mouse connections
        const dxm = particles[i].x - mouse.x;
        const dym = particles[i].y - mouse.y;
        const distm = Math.sqrt(dxm * dxm + dym * dym);
        if (distm < mouseRadius) {
          const alpha = (1 - distm / mouseRadius) * 0.3;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
          ctx.lineWidth = 0.8;
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }

      // Draw particles
      for (const p of particles) {
        const vertFade =
          p.y < fadeStart ? 1 : p.y > fadeEnd ? 0 : 1 - (p.y - fadeStart) / (fadeEnd - fadeStart);

        // Subtle fade in sparse zone (don't hide completely)
        const depth = sparseDepth(p.x, p.y);
        const sparseFade = 1 - depth * 0.5;

        const finalOpacity = p.opacity * vertFade * sparseFade;
        if (finalOpacity < 0.01) continue;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${finalOpacity})`;
        ctx.fill();
      }
    };

    const update = () => {
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;

        // Soft vertical containment
        const softFloor = canvas.height * 0.65;
        if (p.y > softFloor) {
          p.vy -= 0.008;
        }

        // Wrap horizontally
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;

        // Soft bounce off top
        if (p.y < -10) {
          p.y = -10;
          p.vy = Math.abs(p.vy) * 0.5;
        }
        if (p.y > canvas.height * 0.8) {
          p.vy -= 0.015;
        }

        // Gentle push from sparse zone (very mild, just discourages lingering)
        const depth = sparseDepth(p.x, p.y);
        if (depth > 0.3) {
          const zone = getSparseZone();
          const cx = zone.x + zone.w / 2;
          const cy = zone.y + zone.h / 2;
          const dx = p.x - cx;
          const dy = p.y - cy;
          const strength = depth * 0.008;
          p.vx += (dx > 0 ? 1 : -1) * strength;
          p.vy += (dy > 0 ? 1 : -1) * strength;
        }

        // Mouse repulsion
        const dxm = p.x - mouse.x;
        const dym = p.y - mouse.y;
        const dist = Math.sqrt(dxm * dxm + dym * dym);
        if (dist < mouseRadius && dist > 0) {
          const force = ((mouseRadius - dist) / mouseRadius) * 0.02;
          p.vx += (dxm / dist) * force;
          p.vy += (dym / dist) * force;
        }

        // Dampen velocity
        p.vx *= 0.998;
        p.vy *= 0.998;
      }
    };

    const animate = () => {
      update();
      draw();
      animationId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const handleResize = () => {
      resize();
      createParticles();
    };

    resize();
    createParticles();
    animate();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
    };
  }, [theme.palette.primary.main]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

export default ParticleBackground;
